import { Descriptions, Grid, Typography } from "antd";
import dayjs from "dayjs";
import React from "react";

import { FilePreview } from "../../../../../../shared/file-preview/file-preview";
import {
  ApplicantCasesCredentialVerificationArchiveSummaryContainerArchivedCredentialVerificationCaseSummaryFieldsFragment,
  CredentialVerificationCaseV1FinanceDetailsTransactionAdhocTransactions,
} from "../../../../../../../generated";
import { DATE_FORMAT } from "../../../../../../../constants";
import { TransactionData, TransactionsLite } from "../../../../../../shared/transactions/transactions-lite";
import { BackButton } from "../../../../../../shared/back-button";
import { useNavigate } from "react-router-dom";
import {CertificationByApplicantLegalDisclaimer} from "../../../../components/certification-by-applicant-legal-disclaimer";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
const { Title } = Typography;

interface SummaryProps {
  data: ApplicantCasesCredentialVerificationArchiveSummaryContainerArchivedCredentialVerificationCaseSummaryFieldsFragment;
}

export const Summary: React.FC<SummaryProps> = (props) => {
  const navigate = useNavigate();
  // calculate total amount
  let totalSentAmount = 0;
  let totalReceivedAmount = 0;
  if (props.data?.financeDetails?.transactions) {
    const finance = props.data?.financeDetails?.transactions ?? null;
    if (finance) {
      if (finance?.adhocTransactions?.length) {
        finance?.adhocTransactions?.forEach((transaction) => {
          const amount = transaction?.amount;
          if (amount !== undefined && amount !== null) {
            if (amount > 0) {
              totalSentAmount += amount;
            } else if (amount < 0) {
              totalReceivedAmount += Math.abs(amount); // Add to total received if negative
            }
          }
        });
      }
      if (finance?.submission) {
        const submissionAmount = finance?.submission?.amount ?? 0;
        totalSentAmount += submissionAmount;
      }
    }
  }
  const submissionTransaction: CredentialVerificationCaseV1FinanceDetailsTransactionAdhocTransactions =
    {
      amount:
        props.data?.financeDetails?.transactions?.submission?.amount ??
        undefined,
      reason: "submission amount",
      createdAt:
        props.data?.financeDetails?.transactions?.submission
          ?.transactionReference?.completedAt,
      transactionReference: {
        referenceId:
          props.data?.financeDetails?.transactions?.submission
            ?.transactionReference?.referenceId,
      },
      id: props.data.id,
    };

  const transactionData: TransactionData = {
    totalReceived: totalReceivedAmount,
    totalSent: totalSentAmount,
    transactionsData: [
      ...(props.data?.financeDetails?.transactions
        ?.adhocTransactions as CredentialVerificationCaseV1FinanceDetailsTransactionAdhocTransactions[]),
      submissionTransaction,
    ],
  };

  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;

  return (
    <div className="flex flex-col space-y-7 w-full">
      {isMobile && <BackButton onBack={() => navigate('/applicant/cases')} title="Back to Cases" />}

      <div style={{ marginTop: "20px"}}>
        <Title level={4} style={{ display: 'inline' }}>
          Credential Verification Request
        </Title>
      </div>
      <Descriptions layout="vertical">
        <Descriptions.Item label="Submission Date">{dayjs(props.data.submittedAt).format(DATE_FORMAT).toUpperCase()}</Descriptions.Item>
        <Descriptions.Item label="Decision Date">{dayjs(props.data.caseDetails.applicationReview.decision.completedAt).format(DATE_FORMAT).toUpperCase()}</Descriptions.Item>
        <Descriptions.Item label="Case ID">{props.data.id}</Descriptions.Item>
        <Descriptions.Item label="Decision">{props.data.caseDetails.applicationReview.decision.result}</Descriptions.Item>
      </Descriptions>
      <div>
        <Title level={5}>What type of credential are you requesting to be verified?</Title>
        <Descriptions layout="vertical" size="small" column={1}>
          <Descriptions.Item>{props.data.caseDetails.application.credentialType}</Descriptions.Item>
        </Descriptions>
      </div>
      <div>
        <Title level={5}>Issuing Institution</Title>
        <Descriptions layout="vertical" size="small" column={1}>
          <Descriptions.Item label="Institution Name">{props.data.caseDetails.application?.issuingInstitutionName ?? "N/A"}</Descriptions.Item>
        </Descriptions>
      </div>
      <div>
        <Title level={5}>Credential Details</Title>
        <Descriptions layout="vertical" size="small" column={2}>
          <Descriptions.Item label="Credential Title">{props.data.caseDetails.application?.credentialTitle}</Descriptions.Item>
          <Descriptions.Item label="Program (If Applicable)">{props.data.caseDetails.application?.program}</Descriptions.Item>
          <Descriptions.Item label="Name on Credential">{props.data.caseDetails.application?.nameOnCredential}</Descriptions.Item>
          <Descriptions.Item label="Credential Issued Date">
            {dayjs(props.data.caseDetails.application?.dateCredentialIssuedAt).format(DATE_FORMAT).toUpperCase()}
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div>
        <CertificationByApplicantLegalDisclaimer completed={true} />
      </div>

      <Title level={5}>Files</Title>
      <div className="flex w-full gap-5 flex-wrap h-auto">
        {props.data.files
          ?.filter((file) => !file?.name?.endsWith('.json'))
          .map((file) => (
            <FilePreview key={file?.name} fileUrl={file?.url ?? ''} fileName={file?.name?.split('/').pop() ?? ''} fileType={file?.type ?? ''} isShowFileName={false} />
          ))}
      </div>

      <div>
        <TransactionsLite data={transactionData} />
      </div>
    </div>
  );
};
